import { useEffect, useState } from "react";

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  preference: boolean;
  marketing: boolean;
}

export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  preference: false,
  marketing: false,
};

const PREFS_KEY = "cookie-preferences";
const CONSENT_KEY = "cookie-consent";
const EVENT_NAME = "cookie-preferences-changed";

export type ConsentValue = "accepted" | "declined" | "custom";

export function loadCookiePreferences(): CookiePreferences {
  if (typeof window === "undefined") return { ...DEFAULT_COOKIE_PREFERENCES };
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_COOKIE_PREFERENCES };
    const parsed = JSON.parse(raw);
    return {
      essential: true,
      analytics: parsed.analytics ?? false,
      preference: parsed.preference ?? false,
      marketing: parsed.marketing ?? false,
    };
  } catch {
    return { ...DEFAULT_COOKIE_PREFERENCES };
  }
}

export function getConsentValue(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(CONSENT_KEY);
}

export function saveCookiePreferences(
  prefs: CookiePreferences,
  consentValue: ConsentValue,
): void {
  if (typeof window === "undefined") return;
  const normalized: CookiePreferences = { ...prefs, essential: true };
  window.localStorage.setItem(PREFS_KEY, JSON.stringify(normalized));
  window.localStorage.setItem(CONSENT_KEY, consentValue);
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, { detail: normalized }),
  );
}

export function useCookiePreferences(): [
  CookiePreferences,
  (prefs: CookiePreferences) => void,
] {
  const [prefs, setPrefs] = useState<CookiePreferences>(() =>
    loadCookiePreferences(),
  );

  useEffect(() => {
    const sync = () => setPrefs(loadCookiePreferences());
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<CookiePreferences>).detail;
      if (detail) setPrefs({ ...detail, essential: true });
      else sync();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === PREFS_KEY || e.key === CONSENT_KEY) sync();
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return [prefs, setPrefs];
}
